//app/register/driver.tsx
import {
  StyleSheet,
  View,
  Platform,
  AccessibilityInfo,
  Share,
  TextInput as RNTextInput,
} from 'react-native';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  TextInput,
  Button,
  Surface,
  Text,
  useTheme,
  Appbar,
  Portal,
  Dialog,
  ProgressBar,
  IconButton,
} from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { router, useLocalSearchParams } from 'expo-router';
import React, { useEffect, useRef, useState } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { setItem, getItem, removeItem } from '../../src/lib/storage';
import { isCompanyContactRequired } from '../../src/utils/companyContactRule';
import { getDriverOutcomeCopy } from '../../src/utils/driverOutcomeCopy';
import {
  PROFILE_RECOVERY_FIELDS,
  resolveResumeDecision,
} from '../../src/utils/resumeDecision';
import {
  driverCompanyDraftFromState,
  type OnboardingPersona,
} from '../../src/utils/onboardingResume';
import { CountryCodeSelector } from '../../src/components/CountryCodeSelector';
import { ReferralCodeField } from '../../src/components/referrals/ReferralCodeField';
import { isRegistrationSessionRejection, registrationReferralPayload } from '../../src/components/referrals/registrationReferral';
import { beginRegistrationSession } from '../../src/components/referrals/registrationSession';
import { CustomDatePicker } from '../../src/components/CustomDatePicker';
import { NifInput } from '../../src/components/NifInput';
import { OperatorLicenseInput } from '../../src/components/OperatorLicenseInput';
import {
  checkEmailAvailable,
  registerDriver,
  acceptDriverInvitation,
  getDriverInvitation,
  getInvitationByEmail,
  associateDriver,
  advanceOnboardingState,
  getOnboardingState,
  getDriverWorkContext,
  lookupCompany,
  getUserDetails,
  updateUserProfile,
} from '../../src/api/apiService';
import { APP_ORIGIN } from '../../src/api/config';
import { track, resolveEnvironment } from '../../src/lib/analytics';
import { AnalyticsEvent } from '../../src/lib/analyticsEvents';
import { routeForDriverOutcome } from '../../src/utils/driverOutcomeRoute';
import { createSingleFlight } from '../../src/utils/singleFlight';
import { shareLinkCapturedAt, shareLinkReferralCode } from '../../src/utils/shareLinkReferral';
import { clearPendingReferral } from '../../src/utils/pendingReferral';
import { RegistrationAppStoreLinks } from '../../src/components/appStores/AppStoreLinks';
import ChatBubble from '@/src/components/chat/ChatBubble';
import TypingIndicator from '@/src/components/chat/TypingIndicator';
import ChatConversation from '@/src/components/chat/ChatConversation';

// Typed wrapper giving access to react-native-web's dataSet prop without
// falling back to @ts-ignore (same pattern as register/owner.tsx).
type DataSetProps = { dataSet?: Record<string, string | number | null | undefined> };
const DataSetView = View as React.ComponentType<
  React.ComponentPropsWithoutRef<typeof View> & DataSetProps
>;

const removeMask = (value: string): string => value.replace(/\D/g, '');

const applyMaskNipc = (value: string): string =>
  value
    .replace(/\D/g, '')
    .replace(/(\d{3})(\d{3})(\d{3})/, '$1.$2.$3')
    .substring(0, 11);

/**
 * Chat-flow driver registration (#207 — mirrors register/owner.tsx's pattern,
 * #206). One question per screen with a progress bar.
 *
 * Matrix (docs/plans/2026-07-02-milestone-13-registration-paywall.md §2):
 * - C1 (invite link): `token` URL param — unchanged happy path, payer step
 *   still offered (self-pay is reachable immediately since accepting the
 *   invitation creates an ACTIVE employment).
 * - C2 (pending invite matched by email): detected right after the email
 *   step; an inline dialog offers to accept it — same fast path as C1 from
 *   there on.
 * - C3/C4 (no invite yet): the "company" step reuses the existing, tested
 *   POST /api/companies/driver/associate/ (NIPC lookup — notifies the owner
 *   if the company exists (C3), or creates an unclaimed company shell +
 *   emails the contact to register (C4)).
 * - C5 (expired invite): handled in invitations/accept/[token].tsx, not here.
 *
 * Driver onboarding now blocks on the reporting profile fields
 * (NIF/address/CMTVDE/date_of_birth) for both self-pay and company-pay.
 * They are collected inside this chat before the payer step, not deferred to
 * a later profile-completion screen.
 */
type ChatStepId =
  | 'email'
  | 'password'
  | 'name'
  | 'phone'
  | 'nif'
  | 'address'
  | 'cmtvde'
  | 'dateOfBirth'
  | 'payer'
  | 'company';

const STEP_FIELDS: Record<ChatStepId, string[]> = {
  email: ['email'],
  password: ['password', 'confirmPassword'],
  name: ['full_name'],
  phone: ['phoneNumber'],
  nif: ['nif'],
  address: ['address'],
  cmtvde: ['cmtvde'],
  dateOfBirth: ['date_of_birth'],
  payer: ['payer'],
  company: ['companyNipc', 'companyContact'],
};

const ALL_STEP_IDS: ChatStepId[] = [
  'email',
  'password',
  'name',
  'phone',
  'nif',
  'address',
  'cmtvde',
  'dateOfBirth',
  'company',
  'payer',
];

const PROFILE_RECOVERY_STEP_BY_FIELD: Record<string, ChatStepId> = {
  nif: 'nif',
  address: 'address',
  license_number: 'cmtvde',
  date_of_birth: 'dateOfBirth',
};

const PROFILE_RECOVERY_FIELD_BY_STEP: Partial<Record<ChatStepId, string>> = {
  nif: 'nif',
  address: 'address',
  cmtvde: 'license_number',
  dateOfBirth: 'date_of_birth',
};

type MatchedInvitation = { token: string; companyName: string; companyNipc?: string };
// Canonical onboarding outcomes (backend contract H). `unknown` is a defensive
// fallback so an older/newer backend value still renders a sane pending screen.
type OnboardingOutcome =
  | 'active_by_invitation'
  | 'existing_active_employment'
  | 'pending_no_invitation'
  | 'pending_no_license'
  | 'company_contact_submitted'
  | 'failed';

type PendingOutcome = { companyName: string; outcome: OnboardingOutcome; payer: 'self' | 'company' };

export default function RegisterDriver() {
  const { t, i18n: i18nInstance } = useTranslation('register');
  const { t: workCompanyText } = useTranslation('workCompany');
  const theme = useTheme();
  const params = useLocalSearchParams<{
    review_payer?: string;
    email?: string; full_name?: string; phoneNumber?: string;
    nif?: string; address?: string; license_number?: string; date_of_birth?: string; token?: string;
    ref?: string | string[]; ref_at?: string | string[];
  }>();
  const invitationTokenFromUrl = params.token;

  const [countryCode, setCountryCode] = useState('+351');
  const [menuVisible, setMenuVisible] = useState(false);
  const [serverError, setServerError] = useState<{
    email?: string; password?: string; full_name?: string; phone_number?: string;
  } | null>(null);
  const [stepId, setStepId] = useState<ChatStepId>('email');
  const [checking, setChecking] = useState(false);
  const [continuing, setContinuing] = useState(false);
  const continueSingleFlightRef = useRef(createSingleFlight<void>());
  // The resume payer choice submits the single association POST directly; a
  // duplicate/concurrent press must collapse to one association (D-013).
  const associateSingleFlightRef = useRef(createSingleFlight<void>());
  const [typingBeat, setTypingBeat] = useState(false);
  const typingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasBeatRef = useRef(false);
  const advanceTo = (next: ChatStepId) => {
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    hasBeatRef.current = true;
    setTypingBeat(true);
    setStepId(next);
    typingTimerRef.current = setTimeout(() => {
      typingTimerRef.current = null;
      setTypingBeat(false);
    }, 650);
  };
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Steps with more than one input chain Enter/Tab through them instead of
  // submitting the step on the first field.
  const confirmPasswordRef = useRef<RNTextInput>(null);
  const companyNipcRef = useRef<RNTextInput>(null);
  const companyEmailRef = useRef<RNTextInput>(null);
  const companyPhoneRef = useRef<RNTextInput>(null);

  const focusTargetByStep: Partial<Record<ChatStepId, string>> = {
    dateOfBirth: 'chat-input-date-of-birth',
  };

  // Decision 3 (resume, MR !388 finding 7): a partially-authenticated driver who
  // returns (after email-confirmation redirect or a reload) already has a session
  // token. We consult `GET /api/users/me` for the canonical resume contract:
  //   - resume_eligible=true  -> explicit resume mode: prefill the SERVER identity
  //     (never a stale localStorage email), skip register-driver, land on the NIPC
  //     step. Zero POST register-driver, zero associate until the NIPC step runs.
  //   - state='owner'         -> dual-role/owner: NOT a driver resume. Clear the
  //     token and redirect to /onboarding/owner (no associate, no register).
  //   - state='complete'      -> already-finished driver: clear token and redirect
  //     to /dashboard/home (no replay, no associate).
  //   - email_mismatch=true   -> stale/different email: the server identity is
  //     authoritative; we do NOT associate on a mismatched email. Clear cached
  //     identity and (if resume_eligible) resume with the server email only.
  const [resumeMode, setResumeMode] = useState(false);
  const [serverIdentityEmail, setServerIdentityEmail] = useState<string | null>(null);
  const [profileRecoveryMode, setProfileRecoveryMode] = useState(false);
  const [profileRecoverySteps, setProfileRecoverySteps] = useState<ChatStepId[]>([]);
  // Refs so selectPayer never sees a stale false after async resume (MR !388).
  const resumeModeRef = useRef(false);
  const accountReadyRef = useRef(false);
  const serverIdentityEmailRef = useRef<string | null>(null);
  const payerReviewRelationshipRef = useRef<number | null>(null);
  const profileRecoveryModeRef = useRef(false);
  const profileRecoveryStepsRef = useRef<ChatStepId[]>([]);

  // D-057: optional referral code carried from a campaign link into signup. The
  // attribution freezes atomically inside the register-driver request, so the
  // code and its SERVER-issued registration session must be captured BEFORE the
  // account is created — there is no post-signup claim. The code section exists
  // for share-link visitors on web and optional manual entry on native.
  // Invitation/resume journeys stay separate.
  const initialReferral = shareLinkReferralCode(params.ref);
  const initialReferralAt = initialReferral ? shareLinkCapturedAt(params.ref_at) : undefined;
  const [referralToken, setReferralToken] = useState<string | null>(null);
  const referralStateRef = useRef<{ kind: string; code?: string }>({ kind: initialReferral ? 'pending' : 'idle' });
  const referralDraftRef = useRef(initialReferral);
  const [referralRejection, setReferralRejection] = useState('');
  const [referralRecapture, setReferralRecapture] = useState(0);
  // D-057/D-012: referral eligibility is POSITIVE-ONLY. The `userToken` read in the
  // startup effect proves there is no residual identity; with a token we are in an
  // authenticated resume (`resume`) / profile recovery / a redirecting non-driver
  // account, none of which may request or capture a referral. Until that read
  // resolves, eligibility stays false, so an existing account never fires an
  // anonymous registration-session request at mount.
  const [freshSignupProven, setFreshSignupProven] = useState(false);
  const referralActive = (!!initialReferral || Platform.OS !== 'web')
    && freshSignupProven && !invitationTokenFromUrl && !resumeMode && !profileRecoveryMode;
  useEffect(() => {
    if (!referralActive) return;
    let current = true;
    void beginRegistrationSession().then(token => { if (current) setReferralToken(token); });
    return () => { current = false; };
  }, [referralActive]);

  // Duplicate-identity (409) — Dialog, not Alert.alert (no-op on web, DESIGN.md).
  const [dupDialogVisible, setDupDialogVisible] = useState(false);
  const [dupDialogMessage, setDupDialogMessage] = useState('');
  const [dupDialogEmail, setDupDialogEmail] = useState('');

  // Generic unexpected-error dialog (also Dialog-based, never Alert.alert).
  const [errorDialogVisible, setErrorDialogVisible] = useState(false);
  const [errorDialogMessage, setErrorDialogMessage] = useState('');

  // #207 matrix C2: a pending invitation matched by email — offered inline.
  const [matchedInvitation, setMatchedInvitation] = useState<MatchedInvitation | null>(null);
  const [invitationOffer, setInvitationOffer] = useState<MatchedInvitation | null>(null);
  const [invitationLinkNotice, setInvitationLinkNotice] = useState(false);
  // Expected company NIPC bound to the invitation (URL token or email match).
  // Confirmation must match this identity fail-closed before payer/accept.
  const invitedCompanyNipcRef = useRef<string>('');

  // Common NIPC lookup state (decision 2). Backend does the lookup by NIPC
  // before asking for a contact. `companyLookup` holds the result:
  //   null  -> not yet looked up (initial)
  //   {found:false} -> unknown NIPC -> contact (email OR phone) is required
  //   {found:true, name, tax_identifier} -> known company -> ask the driver to
  //     confirm ownership; no corporate contact is requested (decision 1).
  // `companyConfirmed` gates the final associate: a found company must be
  // explicitly confirmed before proceeding.
  const [companyLookup, setCompanyLookup] = useState<{ found: boolean; name?: string; tax_identifier?: string } | null>(null);
  const [workCompanyNameRequired, setWorkCompanyNameRequired] = useState(false);
  const [companyLookupLoading, setCompanyLookupLoading] = useState(false);
  const [companyConfirmed, setCompanyConfirmed] = useState(false);
  const [companyLookupError, setCompanyLookupError] = useState<string | null>(null);
  // True while a server-side driver_company draft is known to exist for this
  // account (restored or just captured). "No / correct it" must clear it before
  // the local reset so a stale draft can never be resurrected on a later login.
  const companyDraftPersistedRef = useRef(false);

  // Submission progress: guards against re-registering on a company-step retry.
  const [accountReady, setAccountReady] = useState(false);
  const [companyStepError, setCompanyStepError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<PendingOutcome | null>(null);

  const recordDriverProgress = async (
    persona: 'DRV-SELF' | 'DRV-CO',
    currentStep: string,
  ): Promise<void> => {
    try {
      if (typeof advanceOnboardingState === 'function') {
        await advanceOnboardingState(persona, currentStep);
      }
    } catch {
      // The pointer is additive only. Domain mutations and entitlement remain
      // authoritative, so a transient pointer failure cannot fabricate state.
    }
  };

  /**
   * Resolve the CURRENT authoritative driver persona/step from the server. A
   * captured/cleared draft must use the server pointer, never a client-invented
   * step (the backend rejects a mismatched transition), and never a stale
   * cached GET (a short-lived cache could otherwise false-green the resume).
   */
  const resolveAuthoritativeDriverPointer = async (): Promise<{
    persona: OnboardingPersona;
    currentStep: string;
  }> => {
    const fallback = { persona: 'DRV-CO' as OnboardingPersona, currentStep: 'company_confirm' };
    if (typeof getOnboardingState !== 'function') return fallback;
    try {
      const res = await getOnboardingState({ fresh: true });
      if (
        res.ok
        && res.data
        && typeof res.data.persona === 'string'
        && typeof res.data.current_step === 'string'
      ) {
        return { persona: res.data.persona, currentStep: res.data.current_step };
      }
    } catch {
      /* the caller's fail-closed handling decides */
    }
    return fallback;
  };

  /**
   * Restore the driver's OWN confirmed-company UX draft (server-resolved fiscal
   * identity + name) on a permitted incomplete, pre-association driver step.
   * Continuity only: it never infers a payer, coverage or relationship, never
   * auto-selects the payer, and never writes company identity from the client.
   */
  const restoreConfirmedCompanyDraft = async (): Promise<void> => {
    if (typeof getOnboardingState !== 'function') return;
    try {
      const res = await getOnboardingState({ fresh: true });
      if (!res.ok || !res.data) return;
      const draft = driverCompanyDraftFromState(res.data);
      if (!draft) return;
      const digits = removeMask(draft.company_nipc);
      if (!digits) return;
      setValue('companyNipc', applyMaskNipc(digits), { shouldValidate: false });
      setValue('companyName', draft.company_name, { shouldValidate: false });
      setCompanyLookup({
        found: true,
        name: draft.company_name,
        tax_identifier: digits,
        __nipc: digits,
      } as any);
      setCompanyLookupLoading(false);
      setCompanyLookupError(null);
      setCompanyConfirmed(true);
      companyDraftPersistedRef.current = true;
    } catch {
      // Best-effort UX continuity only; the explicit payer step and the server
      // association outcome remain authoritative.
    }
  };

  /**
   * Persist the explicit confirmed-company draft BEFORE the payer step, awaiting
   * the server acknowledgement. Fail-closed: a rejected write keeps the driver
   * on the company step with the existing actionable error and associates
   * nothing. Only the server-resolved fiscal identifier is sent.
   */
  const persistConfirmedCompany = async (nipcDigits: string): Promise<boolean> => {
    if (typeof advanceOnboardingState !== 'function' || !nipcDigits) return true;
    setCompanyStepError(null);
    const { persona, currentStep } = await resolveAuthoritativeDriverPointer();
    try {
      const res = await advanceOnboardingState(persona, currentStep, {
        company_confirmation: { company_nipc: nipcDigits, confirmed: true },
      });
      if (!res?.ok) {
        setCompanyStepError(t('companyLookupFailed'));
        return false;
      }
      companyDraftPersistedRef.current = true;
      return true;
    } catch {
      setCompanyStepError(t('companyLookupFailed'));
      return false;
    }
  };

  /**
   * Clear ONLY the driver_company draft (null company_confirmation) for the
   * incomplete pre-association driver, awaiting the server acknowledgement before
   * the local reset. A failed clear is fail-closed (no reset) so the stale draft
   * cannot be resurrected from storage.
   */
  const clearConfirmedCompanyDraft = async (): Promise<boolean> => {
    if (!companyDraftPersistedRef.current || typeof advanceOnboardingState !== 'function') {
      return true;
    }
    const { persona, currentStep } = await resolveAuthoritativeDriverPointer();
    try {
      const res = await advanceOnboardingState(persona, currentStep, {
        company_confirmation: null,
      });
      if (!res?.ok) {
        setCompanyStepError(t('companyLookupFailed'));
        return false;
      }
      companyDraftPersistedRef.current = false;
      return true;
    } catch {
      setCompanyStepError(t('companyLookupFailed'));
      return false;
    }
  };

  const invitationToken = invitationTokenFromUrl || matchedInvitation?.token;
  const hasInvitation = !!invitationToken;

  const shareUrl = `${APP_ORIGIN}/register/owner`;


  useEffect(() => {
    // Top-of-funnel: user landed on the driver registration form (#323).
    track(AnalyticsEvent.SignupStarted, {
      user_role: 'driver',
      entry_point: 'register_driver',
    });
    // Decision 3 (resume): a partially-authenticated driver who returns to this
    // screen (e.g. after the email-confirmation redirect, or a reload) already
    // has a session token. Consult the authoritative `users/me` resume contract
    // BEFORE doing anything else:
    //   - resume_eligible=true  -> explicit resume mode (prefill SERVER identity,
    //     skip register-driver, land on the canonical NIPC step).
    //   - state='owner' / 'complete' -> NOT a driver resume: clear the token and
    //     redirect out (no register, no associate).
    //   - email_mismatch=true -> server identity is authoritative; do not
    //     associate on a stale/different email — resume on the server email only.
    (async () => {
      try {
        const token = await getItem('userToken');
        if (!token) {
          // Fresh signup: normal registration flow (no residual identity). Only
          // this positive proof enables referral capture for the journey.
          setFreshSignupProven(true);
          return;
        }

        const meResp = await getUserDetails();
        // Fail-closed on non-2xx / malformed: quarantine token and block to login.
        // NEVER fall into fresh registration while a residual token exists.
        if (!meResp.ok || !meResp.data) {
          try {
            await removeItem('userToken');
            await removeItem('register:cached_email');
          } catch {
            /* ignore storage errors */
          }
          router.replace('/login');
          return;
        }

        if (params.review_payer === '1' && !invitationTokenFromUrl) {
          const state = await getOnboardingState({ fresh: true });
          const work = await getDriverWorkContext();
          const company = work.data?.work_company;
          const relationshipId = work.data?.relationship_id;
          if (!(state.ok && state.data?.persona === 'DRV-SELF' && state.data.current_step === 'product'
                && state.data.completed === false && work.ok && work.data.license?.payer === 'self'
                && company?.tax_identifier && typeof relationshipId === 'number')) {
            router.replace('/onboarding/state-error');
            return;
          }
          // Restore the authenticated account's recorded company, not a URL or
          // local-storage draft. No association occurs until another explicit choice.
          resumeModeRef.current = true;
          accountReadyRef.current = true;
          setResumeMode(true);
          setAccountReady(true);
          serverIdentityEmailRef.current = meResp.data.email;
          setServerIdentityEmail(meResp.data.email);
          payerReviewRelationshipRef.current = relationshipId;
          setValue('email', meResp.data.email);
          setValue('companyNipc', company.tax_identifier);
          setValue('companyName', company.name);
          setValue('payer', 'self');
          setCompanyLookup({ found: true, name: company.name, tax_identifier: company.tax_identifier });
          setCompanyConfirmed(true);
          setStepId('payer');
          return;
        }
        const decision = resolveResumeDecision(meResp.data as any);

        if (decision.mode === 'block') {
          try {
            await removeItem('userToken');
            await removeItem('register:cached_email');
          } catch {
            /* ignore */
          }
          router.replace(decision.redirect || '/login');
          return;
        }

        if (decision.mode === 'redirect' && decision.redirect) {
          // Dual-role/owner or already-complete driver: this is NOT a driver
          // resume. Clear the cached token so we never associate under it, and
          // route to the correct funnel. Zero register-driver, zero associate.
          try {
            await removeItem('userToken');
          } catch {
            await setItem('userToken', '');
          }
          router.replace(decision.redirect);
          return;
        }

        if (decision.mode === 'profile_recovery') {
          const recoverySteps = Array.from(new Set(
            decision.missingProfileFields.flatMap((field) => {
              if (!PROFILE_RECOVERY_FIELDS.includes(field as (typeof PROFILE_RECOVERY_FIELDS)[number])) {
                return [];
              }
              const step = PROFILE_RECOVERY_STEP_BY_FIELD[field];
              return step ? [step] : [];
            }),
          ));
          if (recoverySteps.length === 0) {
            router.replace('/login');
            return;
          }

          accountReadyRef.current = true;
          setAccountReady(true);
          resumeModeRef.current = true;
          setResumeMode(true);
          profileRecoveryModeRef.current = true;
          setProfileRecoveryMode(true);
          profileRecoveryStepsRef.current = recoverySteps;
          setProfileRecoverySteps(recoverySteps);
          if (decision.serverEmail) {
            serverIdentityEmailRef.current = decision.serverEmail;
            setServerIdentityEmail(decision.serverEmail);
          }

          const serverProfile = (meResp.data as any)?.profile;
          if (serverProfile) {
            if (serverProfile.nif) setValue('nif', serverProfile.nif, { shouldValidate: false });
            if (serverProfile.address) setValue('address', serverProfile.address, { shouldValidate: false });
            if (serverProfile.license_number) {
              setValue('cmtvde', serverProfile.license_number, { shouldValidate: false });
            }
            if (serverProfile.date_of_birth) {
              setValue('date_of_birth', serverProfile.date_of_birth, { shouldValidate: false });
            }
          }
          setStepId(recoverySteps[0]);
          return;
        }

        if (decision.mode === 'resume') {
          // Strict triple gate already enforced by resolveResumeDecision:
          // resume_eligible===true && state==='partial' && valid server email.
          accountReadyRef.current = true;
          setAccountReady(true);
          if (decision.serverEmail) {
            serverIdentityEmailRef.current = decision.serverEmail;
            setServerIdentityEmail(decision.serverEmail);
          }
          if (!invitationTokenFromUrl && !matchedInvitation) {
            resumeModeRef.current = true;
            setResumeMode(true);
            setStepId('company');
            // Restore this account's own confirmed-company draft so a fresh
            // root login / reload does not lose the explicitly confirmed
            // company. It never infers the payer; the explicit payer choice is
            // preserved. Scoped to the own-account path (an invitation keeps its
            // own bind/accept flow, D-006/D-011).
            void restoreConfirmedCompanyDraft();
          }
          return;
        }
        // mode === 'register' is only reachable without a token (handled above).
      } catch {
        // Network/storage failure with a present token: quarantine and block.
        try {
          await removeItem('userToken');
          await removeItem('register:cached_email');
        } catch {
          /* ignore */
        }
        router.replace('/login');
      }
    })();
    return () => {
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!hasBeatRef.current || typingBeat) return;
    if (Platform.OS === 'ios') {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const question = questionFor[stepId]?.question;
      if (question) AccessibilityInfo.announceForAccessibility(question);
    }
    if (Platform.OS !== 'web' || typeof document === 'undefined') return;
    const raf = requestAnimationFrame(() => {
      const focusTarget = focusTargetByStep[stepId] || `chat-input-${stepId}`;
      const el = document.querySelector(
        `[data-testid="${focusTarget}"]`,
      ) as HTMLElement | null;
      el?.focus();
    });
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typingBeat, stepId]);

  const registerDriverSchema = z.object({
    email: z.string().min(1, { message: t('emailRequired') }).email({ message: t('invalidEmail') }),
    password: z.string().min(1, { message: t('passwordRequired') }).min(8, { message: t('passwordTooShort') }),
    confirmPassword: z.string().min(1, { message: t('confirmPasswordRequired') }),
    full_name: z.string().min(1, { message: t('fullNameRequired') }),
    phoneNumber: z
      .string()
      .min(1, { message: t('phoneNumberRequired') })
      .min(8, { message: t('phoneNumberInvalid') })
      .max(15, { message: t('phoneNumberTooLong') })
      .refine(val => /^[\d\s-]+$/.test(val), { message: t('phoneNumberInvalid') }),
    nif: z.string().min(1, { message: t('nifRequired') })
      .refine((value) => removeMask(value).length === 9, { message: t('nifInvalidLength') }),
    address: z.string().trim().min(1, { message: t('addressRequired') }),
    cmtvde: z.string().min(1, { message: t('cmtvdeRequired') })
      .regex(/^\d+\/\d{4}$/, { message: t('cmtvdeInvalid') }),
    date_of_birth: z.string().min(1, { message: t('dateOfBirthRequired') }),
    payer: z.enum(['self', 'company']),

    companyName: z.string().optional(),
    companyNipc: z.string().optional(),
    companyEmail: z.string().optional(),
    companyPhone: z.string().optional(),
  }).superRefine((data, ctx) => {
    if (data.password !== data.confirmPassword) {
      ctx.addIssue({ code: 'custom', path: ['confirmPassword'], message: t('passwordsDoNotMatch') });
    }
    // NIPC resolution is shared by every driver path. A known company supplies
    // its own name; an unknown company needs only one contact, never free-form name.
    if (!data.companyNipc || removeMask(data.companyNipc).length !== 9) {
      ctx.addIssue({ code: 'custom', path: ['companyNipc'], message: t('nipcError') });
    }
    // Decision 1: contact is conditional on the lookup result (organic unknown).
    // Invited drivers bind to the invited company — no free-form unknown lead.
    if (!hasInvitation) {
      if (isCompanyContactRequired(companyLookup, data.companyEmail || '', data.companyPhone || '')) {
        ctx.addIssue({ code: 'custom', path: ['companyEmail'], message: t('companyContactRequired') });
      }
    }
  });

  type RegisterDriverFormData = z.infer<typeof registerDriverSchema>;

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    clearErrors,
    setValue,
    watch,
  } = useForm<RegisterDriverFormData>({
    resolver: zodResolver(registerDriverSchema),
    defaultValues: {
      email: params.email ?? '',
      password: '',
      confirmPassword: '',
      full_name: params.full_name ?? '',
      phoneNumber: params.phoneNumber ?? '',
      nif: params.nif ?? '',
      address: params.address ?? '',
      cmtvde: params.license_number ?? '',
      date_of_birth: params.date_of_birth ?? '',
      payer: undefined as any,

      companyName: '',
      companyNipc: '',
      companyEmail: '',
      companyPhone: '',
    },
  });

  const payer = watch('payer');

  // Resume: bind the authoritative server email into the form once known
  // (setValue is only available after useForm above).
  useEffect(() => {
    if (serverIdentityEmail) {
      setValue('email', serverIdentityEmail, { shouldValidate: false });
    }
  }, [serverIdentityEmail, setValue]);

  // Prefetch invitation company identity for URL-token path so the common NIPC
  // step can prefill expected NIPC and bind confirmation fail-closed.
  useEffect(() => {
    if (!invitationTokenFromUrl) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getDriverInvitation(String(invitationTokenFromUrl));
        if (cancelled || !res.ok || !res.data) return;
        const data = res.data as any;
        if (data?.code === 'invitation_expired') return;
        const nipc = removeMask(String(data.company_nipc || ''));
        const name = String(data.company_name || '');
        if (nipc) {
          invitedCompanyNipcRef.current = nipc;
          setMatchedInvitation((prev) => prev || {
            token: String(invitationTokenFromUrl),
            companyName: name,
            companyNipc: nipc,
          });
          setValue('companyNipc', applyMaskNipc(nipc), { shouldValidate: false });
          if (name) setValue('companyName', name, { shouldValidate: false });
        }
      } catch {
        /* fail-closed: no prefill; driver still types NIPC and confirm binds later */
      }
    })();
    return () => { cancelled = true; };
  }, [invitationTokenFromUrl, setValue]);

  // Common NIPC contract (decision 2 + MR !388 compliance): visual order is
  // pessoais -> company (NIPC) -> payer. At the boundary AFTER personal data
  // and BEFORE NIPC lookup the driver account is created/authenticated once
  // (PRIVATE LOOKUP). Found company never asks for corporate contact. Prefill
  // for invitation is allowed; confirmation still gates accept. No payer/
  // billing before lookup; no second register-driver POST on resume/duplicate.
  const stepOrder: ChatStepId[] = profileRecoveryMode && profileRecoverySteps.length > 0
    ? profileRecoverySteps
    : [
        'email', 'password', 'name', 'phone', 'nif', 'address', 'cmtvde', 'dateOfBirth', 'company', 'payer',
      ];
  const stepIndex = stepOrder.indexOf(stepId);
  const totalSteps = stepOrder.length;
  const isLastStep = stepIndex === totalSteps - 1;

  const validateStep = async (): Promise<boolean> => {
    const data = control._formValues as RegisterDriverFormData;
    const stepErrors: Record<string, string> = {};
    const addIssue = (field: string, message: string) => { stepErrors[field] = message; };

    switch (stepId) {
      case 'email': {
        const res = z.string().min(1, { message: t('emailRequired') }).email({ message: t('invalidEmail') }).safeParse(data.email);
        if (!res.success) addIssue('email', res.error.issues[0].message);
        break;
      }
      case 'password': {
        if (!data.password) addIssue('password', t('passwordRequired'));
        else if (data.password.length < 8) addIssue('password', t('passwordTooShort'));
        if (!data.confirmPassword) addIssue('confirmPassword', t('confirmPasswordRequired'));
        else if (data.password !== data.confirmPassword) addIssue('confirmPassword', t('passwordsDoNotMatch'));
        break;
      }
      case 'name':
        if (!data.full_name) addIssue('full_name', t('fullNameRequired'));
        break;
      case 'phone': {
        const res = z
          .string()
          .min(1, { message: t('phoneNumberRequired') })
          .min(8, { message: t('phoneNumberInvalid') })
          .max(15, { message: t('phoneNumberTooLong') })
          .refine(val => /^[\d\s-]+$/.test(val), { message: t('phoneNumberInvalid') })
          .safeParse(data.phoneNumber);
        if (!res.success) addIssue('phoneNumber', res.error.issues[0].message);
        break;
      }
      case 'nif':
        if (!data.nif) addIssue('nif', t('nifRequired'));
        else if (removeMask(data.nif).length !== 9) addIssue('nif', t('nifInvalidLength'));
        break;
      case 'address':
        if (!(data.address || '').trim()) addIssue('address', t('addressRequired'));
        break;
      case 'cmtvde':
        if (!data.cmtvde) addIssue('cmtvde', t('cmtvdeRequired'));
        else if (!/^\d+\/\d{4}$/.test(data.cmtvde)) addIssue('cmtvde', t('cmtvdeInvalid'));
        break;
      case 'dateOfBirth':
        if (!data.date_of_birth) addIssue('date_of_birth', t('dateOfBirthRequired'));
        break;
      case 'payer':
        if (!data.payer) addIssue('payer', t('payerRequired'));
        break;

      case 'company':
        if (!data.companyNipc || removeMask(data.companyNipc).length !== 9) {
          addIssue('companyNipc', t('nipcError'));
        }
        // Decision 1: contact only mandatory when the NIPC is unknown (organic).
        // While the lookup has NOT resolved yet (companyLookup === null) we
        // must NOT block the continue that triggers the lookup — otherwise
        // runCompanyLookup() never runs, the found/unknown UI never renders,
        // and the flow dead-ends on the NIPC field (MR !388 E2E blocker).
        if (companyLookup !== null) {
          const nipcKnown = companyLookup?.found === true;
          if (!hasInvitation && !nipcKnown) {
            // Unknown company (organic only): one contact is enough for either payer.
            if (!(data.companyEmail || '').trim() && !(data.companyPhone || '').trim()) {
              addIssue('companyEmail', t('companyContactRequired'));
            }
          }
          // Found company must be explicitly confirmed before continue (all paths).
          if (companyLookup?.found === true && !companyConfirmed) {
            addIssue('companyNipc', t('companyLookupConfirmRequired') || 'Confirme a empresa encontrada.');
          }
          // Invited: confirmation MUST bind to the invited company NIPC (fail-closed).
          if (hasInvitation && companyLookup?.found === true && companyConfirmed) {
            const expected = removeMask(
              (matchedInvitation?.companyNipc || invitedCompanyNipcRef.current || '') as string,
            );
            const typed = removeMask(data.companyNipc || '');
            const looked = removeMask(String(companyLookup.tax_identifier || ''));
            if (expected && (typed !== expected || (looked && looked !== expected))) {
              addIssue(
                'companyNipc',
                t('companyLookupInviteMismatch') ||
                  'O NIPC não corresponde à empresa do convite. Corrija antes de continuar.',
              );
            }
          }
        }
        break;
    }

    Object.entries(stepErrors).forEach(([field, message]) => {
      setError(field as any, { type: 'manual', message });
    });
    return Object.keys(stepErrors).length === 0;
  };

  const goToStepWithField = (fields: string[]) => {
    for (const step of stepOrder) {
      if (STEP_FIELDS[step].some(f => fields.includes(f))) {
        setStepId(step);
        return;
      }
    }
  };

  const ASSOCIATION_FIELD_TO_FORM: Record<string, keyof RegisterDriverFormData> = {
    payer: 'payer',
    company_name: 'companyName',
    company_nipc: 'companyNipc',
    company_email: 'companyEmail',
    company_phone: 'companyPhone',
  };

  /**
   * Surface a rejected association as field-specific, actionable validation
   * (D-054). The backend returns an allowlisted
   * `{field, code, detail, recovery, correlation_id}` for payer rejections and
   * the plain DRF shape otherwise. A payer rejection MUST return the driver to
   * the explicit payer decision — never bounce to the company step and never
   * infer a payer — so the required choice is actionable in place.
   */
  const surfaceAssociationError = (errData: any): void => {
    const detail =
      typeof errData?.detail === 'string' && errData.detail.trim()
        ? errData.detail
        : typeof errData?.message === 'string' && errData.message.trim()
          ? errData.message
          : null;
    const code = typeof errData?.code === 'string' ? errData.code : null;
    const field = typeof errData?.field === 'string' ? errData.field : null;

    if (code === 'work_identity_name_required') {
      // Keep the already supplied company identifier and explicit payer. Only
      // collect missing name evidence here, before completing onboarding.
      setWorkCompanyNameRequired(true);
      setStepId('payer');
      return;
    }

    if (field === 'payer' || code === 'payer_required' || code === 'payer_invalid_choice') {
      setError('payer', { type: 'server', message: detail || t('payerRequired') });
      setStepId('payer');
      return;
    }

    const formField = field ? ASSOCIATION_FIELD_TO_FORM[field] : null;
    if (formField) {
      setError(formField, { type: 'server', message: detail || t('unexpectedError') });
      goToStepWithField([String(formField)]);
    }
    setCompanyStepError(detail || t('unexpectedError'));
    setStepId('company');
  };

  const goBack = () => {
    if (stepIndex > 0) {
      setStepId(stepOrder[stepIndex - 1]);
    } else {
      router.back();
    }
  };

  const acceptMatchedInvitation = () => {
    if (!invitationOffer) return;
    setMatchedInvitation(invitationOffer);
    if (invitationOffer.companyNipc) {
      invitedCompanyNipcRef.current = removeMask(invitationOffer.companyNipc);
      setValue('companyNipc', applyMaskNipc(removeMask(invitationOffer.companyNipc)), { shouldValidate: false });
      setValue('companyName', invitationOffer.companyName || '', { shouldValidate: false });
    }
    setInvitationOffer(null);
    advanceTo('password');
  };

  const declineMatchedInvitation = () => {
    setInvitationOffer(null);
    advanceTo('password');
  };

  /**
   * PRIVATE LOOKUP boundary (MR !388 compliance): after personal data and BEFORE
   * NIPC lookup/create, register+authenticate the driver once, persist token,
   * and never POST register-driver a second time (duplicate/resume fail-closed).
   * No payer/billing before lookup. Invitation: new invite driver registers here
   * too; existing authenticated invite uses the already-persisted token.
   */
  const ensureDriverAccountBeforeCompany = async (
    data: RegisterDriverFormData,
  ): Promise<boolean> => {
    if (
      accountReadyRef.current ||
      accountReady ||
      resumeModeRef.current ||
      resumeMode
    ) {
      return true;
    }
    try {
      setServerError(null);
      const referralPayload = registrationReferralPayload(referralStateRef.current, referralToken);
      if (referralActive && !referralPayload) {
        setStepId('email');
        setErrorDialogMessage(t('partner:codeFieldResolve'));
        setErrorDialogVisible(true);
        return false;
      }
      const payload = {
        email: data.email,
        password: data.password,
        full_name: data.full_name,
        phone_number: `${countryCode}${data.phoneNumber}`,
        language: i18nInstance.language,
        profile: {
          nif: removeMask(data.nif),
          address: data.address.trim(),
          license_number: data.cmtvde.trim(),
          date_of_birth: data.date_of_birth,
        },
        // D-057: the referral code and its SERVER-issued registration session
        // travel IN the signup request, so the backend freezes attribution
        // atomically with account creation (never a post-signup claim).
        ...(referralActive ? referralPayload : {}),
      };
      const response = await registerDriver(payload, invitationToken as string | undefined);
      if (!response.ok) {
        const responseData = response.data as any;
        if (responseData?.referral_code && isRegistrationSessionRejection(responseData)) {
          // F10: the session expired, not the code. A fresh session makes the
          // field re-capture the accepted code before the next submission.
          setReferralToken(await beginRegistrationSession());
          setReferralRecapture(n => n + 1);
          setStepId('email');
          setErrorDialogMessage(t('partner:codeFieldSessionExpired'));
          setErrorDialogVisible(true);
          return false;
        }
        if (responseData?.referral_code) {
          referralStateRef.current = { kind: 'invalid' };
          const detail = Array.isArray(responseData.referral_code) ? responseData.referral_code[0] : responseData.referral_code;
          const message = typeof detail === 'string' ? detail : t('partner:codeFieldResolve');
          setReferralRejection(message);
          setStepId('email');
          setErrorDialogMessage(message);
          setErrorDialogVisible(true);
          return false;
        }
        if (response.status === 409 && responseData?.code === 'duplicate_identity') {
          setDupDialogMessage(responseData.detail || t('duplicateIdentityMessage'));
          setDupDialogEmail(data.email);
          setDupDialogVisible(true);
          return false;
        }
        if (responseData && typeof responseData === 'object') {
          handleFieldErrors(responseData);
        } else {
          setErrorDialogMessage(t('unexpectedError'));
          setErrorDialogVisible(true);
        }
        return false;
      }
      track(AnalyticsEvent.SignupCompleted, {
        user_role: 'driver',
        auth_method: invitationToken ? 'invitation' : 'email',
        app_variant: resolveEnvironment(),
      });
      // ALL-22: the device's deferred referral was used (or not) by this signup.
      await clearPendingReferral();
      const authToken = (response.data as any)?.token as string | undefined;
      if (!authToken) {
        // Account may exist without a session token — fail closed before lookup.
        router.push('/register/success');
        return false;
      }
      await setItem('userToken', authToken);
      accountReadyRef.current = true;
      setAccountReady(true);
      await recordDriverProgress('DRV-CO', 'company_confirm');
      return true;
    } catch {
      setErrorDialogMessage(t('unexpectedError'));
      setErrorDialogVisible(true);
      return false;
    }
  };

  const runCompanyLookup = async (nipc: string) => {
    setCompanyLookupLoading(true);
    setCompanyLookupError(null);
    try {
      const res = await lookupCompany(nipc);
      if (res.ok && res.data) {
        // Stash the looked-up NIPC so handleContinue can detect a changed NIPC and
        // re-lookup instead of reusing a stale result.
        setCompanyLookup({ found: !!res.data.found, name: res.data.name, tax_identifier: res.data.tax_identifier, __nipc: nipc } as any);
        setCompanyConfirmed(false);
        // A found company pre-fills its own name and needs no contact.
        if (res.data.found) {
          setValue('companyName', res.data.name || '');
          setValue('companyEmail', '');
          setValue('companyPhone', '');
        } else {
          // Unknown: keep whatever contact the driver may type; don't prefill name.
          if (!control._formValues.companyName) {
            setValue('companyName', '');
          }
        }
      } else {
        setCompanyLookupError(t('companyLookupFailed') || 'Não foi possível verificar a empresa.');
      }
    } catch {
      setCompanyLookupError(t('companyLookupFailed') || 'Não foi possível verificar a empresa.');
    } finally {
      setCompanyLookupLoading(false);
    }
  };

  const handleProfileRecoverySubmit = async () => {
    const data = control._formValues as RegisterDriverFormData;
    const profile: Record<string, string> = {};
    const dateToApi = (value: string): string => {
      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
      const match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      return match ? `${match[3]}-${match[2]}-${match[1]}` : value;
    };

    profileRecoveryStepsRef.current.forEach((step) => {
      const field = PROFILE_RECOVERY_FIELD_BY_STEP[step];
      if (!field) return;
      if (field === 'nif') profile[field] = removeMask(data.nif || '');
      if (field === 'address') profile[field] = (data.address || '').trim();
      if (field === 'license_number') profile[field] = (data.cmtvde || '').trim();
      if (field === 'date_of_birth') profile[field] = dateToApi(data.date_of_birth || '');
    });

    try {
      const updateResponse = await updateUserProfile(profile);
      if (!updateResponse.ok) {
        const errorData = updateResponse.data as any;
        const profileErrors = errorData?.profile;
        if (profileErrors && typeof profileErrors === 'object') {
          const formFields: string[] = [];
          const fieldMap: Record<string, keyof RegisterDriverFormData> = {
            nif: 'nif',
            address: 'address',
            license_number: 'cmtvde',
            date_of_birth: 'date_of_birth',
          };
          Object.entries(profileErrors).forEach(([field, message]) => {
            const formField = fieldMap[field];
            if (!formField) return;
            setError(formField, {
              type: 'server',
              message: String(Array.isArray(message) ? message[0] : message),
            });
            formFields.push(formField);
          });
          if (formFields.length > 0) {
            goToStepWithField(formFields);
            return;
          }
        }
        setErrorDialogMessage(errorData?.detail || t('unexpectedError'));
        setErrorDialogVisible(true);
        return;
      }

      // The PATCH response is not the resume authority. Re-read the complete
      // server state so a stale/partial response cannot route into billing.
      const refreshed = await getUserDetails({ fresh: true });
      if (!refreshed.ok || !refreshed.data) {
        setErrorDialogMessage(t('unexpectedError'));
        setErrorDialogVisible(true);
        return;
      }
      const decision = resolveResumeDecision(refreshed.data as any);
      if (decision.mode === 'redirect' && decision.redirect) {
        router.replace(decision.redirect);
        return;
      }
      if (decision.mode === 'profile_recovery') {
        const nextSteps = Array.from(new Set(
          decision.missingProfileFields.flatMap((field) => {
            const step = PROFILE_RECOVERY_STEP_BY_FIELD[field];
            return step ? [step] : [];
          }),
        ));
        if (nextSteps.length > 0) {
          profileRecoveryStepsRef.current = nextSteps;
          setProfileRecoverySteps(nextSteps);
          setStepId(nextSteps[0]);
          return;
        }
      }
      setErrorDialogMessage(t('unexpectedError'));
      setErrorDialogVisible(true);
    } catch {
      setErrorDialogMessage(t('unexpectedError'));
      setErrorDialogVisible(true);
    }
  };

  const continueCurrentStep = async () => {
    const isValid = await validateStep();
    if (!isValid) return;
    const data = control._formValues as RegisterDriverFormData;

    if (profileRecoveryModeRef.current || profileRecoveryMode) {
      if (isLastStep) {
        await handleProfileRecoverySubmit();
        return;
      }
      advanceTo(stepOrder[stepIndex + 1]);
      return;
    }

    if (stepId === 'email') {
      if (referralActive && !registrationReferralPayload(referralStateRef.current, referralToken)) {
        setErrorDialogMessage(t('partner:codeFieldResolve'));
        setErrorDialogVisible(true);
        return;
      }
      setChecking(true);
      try {
        const res = await checkEmailAvailable(data.email);
        if (!res.ok) {
          const errData: any = res.data as any;
          setErrorDialogMessage(errData?.detail || errData?.message || t('unexpectedError'));
          setErrorDialogVisible(true);
          return;
        }
        if (!res.data.available) {
          setError('email' as any, { type: 'server', message: res.data.reason || t('invalidEmail') });
          return;
        }
        // #207 matrix C2: only look up a pending invite if we don't already
        // know one (URL token, or already offered/declined in this session).
        if (!invitationTokenFromUrl && !matchedInvitation) {
          const inv = await getInvitationByEmail(data.email);
          if (inv.ok && inv.data.has_pending) {
            if (inv.data.invitation) {
              setInvitationOffer({
                token: inv.data.invitation.token,
                companyName: inv.data.invitation.company_name,
                companyNipc: inv.data.invitation.company_nipc || undefined,
              });
              return; // Dialog buttons drive the advance from here.
            }
            if (inv.data.requires_invitation_link) {
              setInvitationLinkNotice(true);
              return;
            }
          }
        }
      } finally {
        setChecking(false);
      }
    }

    if (stepId === 'company') {
      const nipcDigits = removeMask(data.companyNipc || '');
      // Decision 2 + invited bind: NIPC lookup BEFORE payer for ALL paths.
      // Invitation may prefill expected NIPC, but confirmation is still required
      // and mismatch against the invited company is fail-closed.
      // PRIVATE LOOKUP: create/authenticate account once before first lookup.
      const ready = await ensureDriverAccountBeforeCompany(data);
      if (!ready) {
        return;
      }
      const lookedUpNipc = (companyLookup as any)?.__nipc as string | undefined;
      const needsLookup = lookedUpNipc !== nipcDigits;
      if (needsLookup) {
        await runCompanyLookup(nipcDigits);
        return;
      }
      if (companyLookup?.found === true && !companyConfirmed) {
        return;
      }
      if (hasInvitation) {
        const expected = removeMask(
          (matchedInvitation?.companyNipc || invitedCompanyNipcRef.current || '') as string,
        );
        const looked = removeMask(String(companyLookup?.tax_identifier || ''));
        if (!companyLookup?.found) {
          setCompanyLookupError(
            t('companyLookupInviteMismatch') ||
              'O NIPC não corresponde à empresa do convite. Corrija antes de continuar.',
          );
          setCompanyConfirmed(false);
          return;
        }
        if (expected && (nipcDigits !== expected || (looked && looked !== expected))) {
          setCompanyLookupError(
            t('companyLookupInviteMismatch') ||
              'O NIPC não corresponde à empresa do convite. Corrija antes de continuar.',
          );
          setCompanyConfirmed(false);
          return;
        }
      }
      // Lookup done and (found->confirmed OR organic unknown). Proceed.
    }

    if (isLastStep) {
      await handleSubmit(handleRegister, (formErrors) => {
        goToStepWithField(Object.keys(formErrors));
      })();
      return;
    }
    advanceTo(stepOrder[stepIndex + 1]);
  };

  const handleContinue = () => continueSingleFlightRef.current(async () => {
    setContinuing(true);
    try {
      await continueCurrentStep();
    } finally {
      setContinuing(false);
    }
  });

  const selectPayer = (value: 'self' | 'company') => {
    setValue('payer', value, { shouldValidate: false });
    clearErrors(['payer'] as any);
    if (value === 'self' && workCompanyNameRequired && !(control._formValues.companyName || '').trim()) {
      setError('companyName', { type: 'required', message: workCompanyText('companyName') });
      return;
    }
    // No `payer_selected` event (not canonical). The chosen payer is attached
    // as the `payer` property on onboarding_completed / checkout_started.
    // Company-pay OR self-pay both declare the employer in the common NIPC step
    // (contract: every driver informs a NIPC before the paywall decision). The
    // company step already ran before this one, so selecting the payer is the
    // final action: submit the associate call (carrying payer) directly.
    // No further step exists after payer (finding 3: selectPayer submits).
    //
    // Resume mode (MR !388 blocker C/D): the account already exists. Never run
    // full-schema handleSubmit (would bounce to email). Associate DIRECTLY from
    // the current form values + server identity — no register-driver POST.
    const isResuming =
      resumeModeRef.current ||
      accountReadyRef.current ||
      resumeMode ||
      accountReady ||
      Boolean(serverIdentityEmailRef.current || serverIdentityEmail);
    if (isResuming && hasInvitation) {
      // Invited resume still goes through register/accept path via handleSubmit,
      // never organic associate (bind stays invitation-company fail-closed).
      handleSubmit(handleRegister, (formErrors) => {
        goToStepWithField(Object.keys(formErrors));
      })();
      return;
    }
    if (isResuming) {
      const data = control._formValues as RegisterDriverFormData;
      // Single-flight: a duplicate/concurrent payer press must collapse to a
      // single association POST (D-013, no duplicate association).
      void associateSingleFlightRef.current(async () => {
        setContinuing(true);
        try {
          setCompanyStepError(null);
          const assocRes = await associateDriver({
            company_name: data.companyName || '',
            company_nipc: removeMask(data.companyNipc || ''),
            company_email: (data.companyEmail || '').trim() || undefined,
            company_phone: (data.companyPhone || '').trim() || undefined,
            payer: value,
            ...(value === 'company' && payerReviewRelationshipRef.current !== null ? {
              reconsider_initial_payer: true, expected_relationship_id: payerReviewRelationshipRef.current,
            } : {}),
            // Reuse the canonical prospective work declaration, not a lead-only
            // outcome, after the driver explicitly selects personal funding.
            ...(value === 'self' && companyLookup?.found === false && removeMask(data.companyNipc || '').startsWith('5')
              ? { work_identity_kind: 'collective' as const } : {}),
          });
          if (!assocRes.ok) {
            // Field-specific, actionable validation (D-054). A payer rejection
            // returns the driver to the explicit payer decision, never to a
            // silent company bounce and never inferring a choice.
            surfaceAssociationError(assocRes.data);
            return;
          }
          const resolvedOutcome =
            (assocRes.data.outcome as OnboardingOutcome) || 'pending_no_invitation';
          if (value === 'self') {
            await recordDriverProgress('DRV-SELF', 'product');
            track(AnalyticsEvent.OnboardingCompleted, {
              user_role: 'driver',
              flow: 'self_pay',
              onboarding_outcome: resolvedOutcome,
            });
            router.replace('/dashboard/self-pay');
            return;
          }
          await recordDriverProgress('DRV-CO', resolvedOutcome);
          setOutcome({
            companyName: assocRes.data.company_name || data.companyName || '',
            outcome: resolvedOutcome,
            payer: value,
          });
          track(
            resolvedOutcome === 'active_by_invitation'
              ? AnalyticsEvent.ActivationCompleted
              : AnalyticsEvent.OnboardingCompleted,
            { user_role: 'driver', flow: 'company_pay', onboarding_outcome: resolvedOutcome,
              payer: value, app_variant: resolveEnvironment() },
          );
        } catch {
          setErrorDialogMessage(t('unexpectedError'));
          setErrorDialogVisible(true);
        } finally {
          setContinuing(false);
        }
      });
      return;
    }
    handleSubmit(handleRegister, (formErrors) => goToStepWithField(Object.keys(formErrors)))();
  };

  const handleFieldErrors = (errorsFromServer: Record<string, any>) => {
    const erroredFields: string[] = [];
    let dialogShown = false;
    Object.entries(errorsFromServer).forEach(([key, value]) => {
      if (key === 'detail' || key.startsWith('_')) return;
      if (key === 'profile' && typeof value === 'object' && value !== null) {
        const profileFieldMap: Record<string, keyof RegisterDriverFormData> = {
          nif: 'nif',
          address: 'address',
          license_number: 'cmtvde',
          date_of_birth: 'date_of_birth',
        };
        const profileFieldsWithErrors: string[] = [];
        Object.entries(value).forEach(([profileKey, profileValue]) => {
          const formFieldName = profileFieldMap[profileKey];
          if (!formFieldName) return;
          const message = Array.isArray(profileValue) ? profileValue[0] : profileValue;
          profileFieldsWithErrors.push(formFieldName);
          setError(formFieldName, { type: 'server', message: String(message) });
        });
        if (profileFieldsWithErrors.length > 0) {
          goToStepWithField(profileFieldsWithErrors);
          dialogShown = true;
          return;
        }
        const messages = Object.values(value).map(v => (Array.isArray(v) ? v[0] : v)).join(' ');
        setErrorDialogMessage(messages || t('unexpectedError'));
        setErrorDialogVisible(true);
        dialogShown = true;
        return;
      }
      const message = Array.isArray(value) ? value[0] : value;
      let formFieldName: keyof RegisterDriverFormData | null = null;
      switch (key) {
        case 'email': formFieldName = 'email'; break;
        case 'password': formFieldName = 'password'; break;
        case 'full_name': formFieldName = 'full_name'; break;
        case 'phone_number': formFieldName = 'phoneNumber'; break;
        default: return;
      }
      erroredFields.push(formFieldName);
      setError(formFieldName, { type: 'server', message });
    });
    if (erroredFields.length > 0) {
      goToStepWithField(erroredFields);
      return;
    }
    if (dialogShown) return;
    // Nothing mapped to a step — e.g. a bare {"detail": ...} from a 4xx/5xx.
    // Without this the chat just sits on the current question with no visible
    // feedback: a silent dead end, exactly what the funnel must never do.
    const detail = errorsFromServer.detail;
    setErrorDialogMessage(
      typeof detail === 'string' && detail.trim() ? detail : t('unexpectedError'),
    );
    setErrorDialogVisible(true);
  };

  const handleRegister: SubmitHandler<RegisterDriverFormData> = async (data) => {
    try {
      setServerError(null);
      // Account must already exist (created once at the NIPC boundary). Fail-closed
      // if somehow missing — never a second register-driver POST after lookup.
      const ready = await ensureDriverAccountBeforeCompany(data);
      if (!ready) {
        return;
      }

      if (hasInvitation) {
        // Fail-closed bind: invitation accept only after NIPC lookup + explicit
        // confirmation of the INVITED company. Mismatch never reaches accept.
        const expected = removeMask(
          (matchedInvitation?.companyNipc || invitedCompanyNipcRef.current || '') as string,
        );
        const typed = removeMask(data.companyNipc || '');
        const looked = removeMask(String(companyLookup?.tax_identifier || ''));
        if (
          !companyConfirmed ||
          companyLookup?.found !== true ||
          !expected ||
          typed !== expected ||
          (looked && looked !== expected)
        ) {
          setCompanyStepError(
            t('companyLookupInviteMismatch') ||
              'O NIPC não corresponde à empresa do convite. Corrija antes de continuar.',
          );
          setStepId('company');
          setCompanyConfirmed(false);
          return;
        }
        const acceptRes = await acceptDriverInvitation(invitationToken as string);
        if (!acceptRes.ok) {
          const acceptData = acceptRes.data as any;
          const acceptOutcome = acceptData?.outcome as OnboardingOutcome | undefined;
          if (acceptOutcome === 'pending_no_license' || acceptOutcome === 'failed') {
            await recordDriverProgress(
              'DRV-CO',
              acceptOutcome === 'failed' ? 'pending_no_invitation' : acceptOutcome,
            );
            track(AnalyticsEvent.OnboardingCompleted, {
                user_role: 'driver', flow: 'company_pay', onboarding_outcome: acceptOutcome,
                payer: data.payer, app_variant: resolveEnvironment(),
              });
            setOutcome({
              companyName:
                acceptData?.company_name || matchedInvitation?.companyName || '',
              outcome: acceptOutcome,
              payer: data.payer,
            });
            return;
          }
          setErrorDialogMessage(
            typeof acceptData?.detail === 'string' && acceptData.detail.trim()
              ? acceptData.detail
              : t('unexpectedError'),
          );
          setErrorDialogVisible(true);
          return;
        }
        if (data.payer === 'self') {
          // Invitation acceptance creates an active company employment. Keep the
          // explicit self-pay intent after that server mutation so another
          // device resumes at checkout instead of seeing completed company-pay.
          // This pointer records intent only; the checkout webhook remains the
          // sole source of self-pay entitlement.
          await recordDriverProgress('DRV-SELF', 'product');
          track(AnalyticsEvent.OnboardingCompleted, {
            user_role: 'driver', flow: 'self_pay', onboarding_outcome: 'active_by_invitation',
            payer: data.payer, app_variant: resolveEnvironment(),
          });
          router.replace('/dashboard/self-pay');
        } else {
          await recordDriverProgress('DRV-CO', 'active_by_invitation');
          track(AnalyticsEvent.ActivationCompleted, {
            user_role: 'driver', flow: 'company_pay', onboarding_outcome: 'active_by_invitation',
            payer: data.payer, app_variant: resolveEnvironment(),
          });
          router.push('/register/success');
        }
        return;

      }

      if (!hasInvitation) {
        setCompanyStepError(null);
        const assocRes = await associateDriver({
          company_name: data.companyName || '',
          company_nipc: removeMask(data.companyNipc!),
          company_email: (data.companyEmail || '').trim() || undefined,
          company_phone: (data.companyPhone || '').trim() || undefined,
          payer: data.payer,
          ...(data.payer === 'self' && companyLookup?.found === false && removeMask(data.companyNipc || '').startsWith('5')
            ? { work_identity_kind: 'collective' as const } : {}),
        });
        if (!assocRes.ok) {
          surfaceAssociationError(assocRes.data);
          return;
        }
        const resolvedOutcome = (assocRes.data.outcome as OnboardingOutcome) || 'pending_no_invitation';
        if (data.payer === 'self') {
          await recordDriverProgress('DRV-SELF', 'product');
          // Self-pay: the employer is declared, but activation is the driver's own
          // license bought on the shared surface. Go straight to checkout.
          track(AnalyticsEvent.OnboardingCompleted, {
            user_role: 'driver', flow: 'self_pay', onboarding_outcome: resolvedOutcome,
            payer: data.payer, app_variant: resolveEnvironment(),
          });
          router.replace('/dashboard/self-pay');
          return;
        }
        await recordDriverProgress('DRV-CO', resolvedOutcome);
        setOutcome({
          companyName: assocRes.data.company_name || data.companyName!,
          outcome: resolvedOutcome,
          payer: data.payer,
        });
        // Canonical outcome surfaced to analytics (contract H). Activation maps
        // to the funnel's activation_completed; everything else is a completed
        // onboarding still pending on the company's side.
        track(
          resolvedOutcome === 'active_by_invitation'
            ? AnalyticsEvent.ActivationCompleted
            : AnalyticsEvent.OnboardingCompleted,
          { user_role: 'driver', flow: 'company_pay', onboarding_outcome: resolvedOutcome,
            payer: data.payer, app_variant: resolveEnvironment() },
        );
      }
    } catch (error) {
      setErrorDialogMessage(t('unexpectedError'));
      setErrorDialogVisible(true);
    }
  };

  const handleShare = async () => {
    try {
      await Share.share({ message: shareUrl, url: shareUrl });
    } catch {
      // No-op — the link stays visible on screen for manual copy.
    }
  };

  const fieldError = (field: keyof RegisterDriverFormData, serverKey?: keyof NonNullable<typeof serverError>) => {
    const server = serverKey ? serverError?.[serverKey] : undefined;
    const form = errors[field]?.message as string | undefined;
    if (!server && !form) return null;
    return (
      <Text style={{ color: theme.colors.error, marginTop: 5 }} accessibilityRole="alert">
        {server || form}
      </Text>
    );
  };

  const questionFor: Record<ChatStepId, { question: string; hint?: string }> = {
    email: {
      question: t('chatEmailQuestion'),
      hint: t('chatEmailHint'),
    },
    password: {
      question: t('chatPasswordQuestion'),
      hint: t('chatPasswordHint'),
    },
    name: { question: t('chatNameQuestion') },
    phone: {
      question: t('chatPhoneQuestion'),
      hint: t('chatDriverPhoneHint'),
    },
    nif: {
      question: t('chatNifQuestion'),
    },
    address: {
      question: t('chatAddressQuestion'),
    },
    cmtvde: {
      question: t('chatCmtvdeQuestion'),
      hint: t('chatLicenseHint'),
    },
    dateOfBirth: {
      question: t('chatDobQuestion'),
      hint: t('chatDobHint'),
    },
    payer: {
      question: t('chatPayerQuestion'),
      hint: t('chatPayerHint'),
    },
    company: {
      question: t('chatCompanyQuestion'),
      hint: t('chatCompanyHint'),
    },
  };

  // Enter (web) / the keyboard action key (native) answers the current
  // question — same effect as pressing "Continuar". Without this the chat
  // could only be advanced with the mouse.
  const submitOnEnter = {
    returnKeyType: (isLastStep ? 'done' : 'next') as 'done' | 'next',
    onSubmitEditing: () => {
      void handleContinue();
    },
  };

  // Focus the next input of a multi-input step instead of submitting it.
  const focusNext = (ref: React.RefObject<RNTextInput | null>) => ({
    returnKeyType: 'next' as const,
    blurOnSubmit: false,
    onSubmitEditing: () => ref.current?.focus(),
  });

  // The show/hide toggle sits after its input in the DOM, so leaving it in the
  // tab sequence swallowed the Tab that should go password → confirmation.
  // It stays clickable/tappable and screen-reader reachable.
  const revealToggleProps = { focusable: false } as const;

  const renderStepInputFor = (renderId: ChatStepId) => {
    switch (renderId) {
      case 'email':
        return (
          <Controller
            control={control}
            name="email"
            render={({ field: { onChange, onBlur, value } }) => (
              <>
                <TextInput
                  label={t('emailPlaceholder')}
                  mode="outlined"
                  disabled={typingBeat}
                  autoCapitalize="none"
                  autoFocus
                  keyboardType="email-address"
                  onBlur={onBlur}
                  onChangeText={(text) => {
                    onChange(text.toLowerCase());
                    setServerError(prev => prev ? { ...prev, email: undefined } : null);
                  }}
                  value={value}
                  error={!!errors.email || !!serverError?.email}
                  style={styles.input}
                  testID="chat-input-email"
                  {...submitOnEnter}
                />
                {fieldError('email', 'email')}
                {referralActive && (
                  <View style={styles.referralBlock} testID="driver-referral-block">
                    <ReferralCodeField
                      registrationToken={referralToken}
                      initialCode={referralDraftRef.current}
                      linkCapturedAt={initialReferralAt}
                      serverRejected={referralRejection}
                      recaptureSignal={referralRecapture}
                      onDraftChange={code => { referralDraftRef.current = code; setReferralRejection(''); }}
                      onRegistrationRetry={async () => {
                        const token = await beginRegistrationSession();
                        setReferralToken(token);
                        return token;
                      }}
                      onChange={next => { referralStateRef.current = next; }}
                    />
                  </View>
                )}
              </>
            )}
          />
        );
      case 'password':
        return (
          <>
            <Controller
              control={control}
              name="password"
              render={({ field: { onChange, onBlur, value } }) => (
                <>
                  <TextInput
                    label={t('passwordPlaceholder')}
                    mode="outlined"
                    disabled={typingBeat}
                    secureTextEntry={!showPassword}
                    autoCapitalize="none"
                    onBlur={onBlur}
                    onChangeText={(text) => {
                      onChange(text);
                      setServerError(prev => prev ? { ...prev, password: undefined } : null);
                    }}
                    value={value}
                    error={!!errors.password || !!serverError?.password}
                    style={styles.input}
                    testID="chat-input-password"
                    {...focusNext(confirmPasswordRef)}
                    right={
                      <TextInput.Icon
                        icon={showPassword ? 'eye-off' : 'eye'}
                        disabled={typingBeat}
                        onPress={() => setShowPassword(!showPassword)}
                        tabIndex={-1}
                        {...revealToggleProps}
                      />
                    }
                  />
                  {fieldError('password', 'password')}
                </>
              )}
            />
            <Controller
              control={control}
              name="confirmPassword"
              render={({ field: { onChange, onBlur, value } }) => (
                <>
                  <TextInput
                    ref={confirmPasswordRef}
                    label={t('confirmPasswordPlaceholder')}
                    mode="outlined"
                    disabled={typingBeat}
                    secureTextEntry={!showConfirmPassword}
                    autoCapitalize="none"
                    onBlur={onBlur}
                    onChangeText={onChange}
                    value={value}
                    error={!!errors.confirmPassword}
                    style={styles.input}
                    testID="chat-input-confirm-password"
                    {...submitOnEnter}
                    right={
                      <TextInput.Icon
                        icon={showConfirmPassword ? 'eye-off' : 'eye'}
                        disabled={typingBeat}
                        onPress={() => setShowConfirmPassword(!showConfirmPassword)}
                        tabIndex={-1}
                        {...revealToggleProps}
                      />
                    }
                  />
                  {fieldError('confirmPassword')}
                </>
              )}
            />
          </>
        );
      case 'name':
        return (
          <Controller
            control={control}
            name="full_name"
            render={({ field: { onChange, onBlur, value } }) => (
              <>
                <TextInput
                  label={t('fullNamePlaceholder')}
                  mode="outlined"
                  disabled={typingBeat}
                  onBlur={onBlur}
                  onChangeText={(text) => {
                    onChange(text);
                    setServerError(prev => prev ? { ...prev, full_name: undefined } : null);
                  }}
                  value={value}
                  error={!!errors.full_name || !!serverError?.full_name}
                  style={styles.input}
                  testID="chat-input-name"
                  {...submitOnEnter}
                />
                {fieldError('full_name', 'full_name')}
              </>
            )}
          />
        );
      case 'phone':
        return (
          <Controller
            control={control}
            name="phoneNumber"
            render={({ field: { onChange, onBlur, value } }) => (
              <>
                <View style={styles.phoneInputContainer}>
                  <Button
                    mode="outlined"
                    disabled={typingBeat}
                    onPress={() => setMenuVisible(true)}
                    style={styles.countryCodeButton}
                  >
                    {countryCode}
                  </Button>
                  <TextInput
                    label={t('phoneNumberPlaceholder')}
                    mode="outlined"
                    disabled={typingBeat}
                    keyboardType="phone-pad"
                    onBlur={onBlur}
                    onChangeText={(text) => {
                      onChange(text);
                      setServerError(prev => prev ? { ...prev, phone_number: undefined } : null);
                    }}
                    value={value}
                    error={!!errors.phoneNumber || !!serverError?.phone_number}
                    style={styles.phoneInput}
                    maxLength={15}
                    testID="chat-input-phone"
                    {...submitOnEnter}
                  />
                </View>
                <CountryCodeSelector
                  visible={menuVisible}
                  onDismiss={() => setMenuVisible(false)}
                  onSelect={setCountryCode}
                  selectedCode={countryCode}
                />
                {fieldError('phoneNumber', 'phone_number')}
              </>
            )}
          />
        );
      case 'nif':
        return (
          <Controller
            control={control}
            name="nif"
            render={({ field: { onChange, onBlur, value } }) => (
              <NifInput
                label={t('nifPlaceholder')}
                value={value}
                onChange={(text) => {
                  clearErrors('nif');
                  onChange(text);
                }}
                onBlur={onBlur}
                style={styles.input}
                errorText={errors.nif?.message}
                testID="chat-input-nif"
                onSubmitEditing={submitOnEnter.onSubmitEditing}
                returnKeyType={submitOnEnter.returnKeyType}
              />
            )}
          />
        );
      case 'address':
        return (
          <Controller
            control={control}
            name="address"
            render={({ field: { onChange, onBlur, value } }) => (
              <>
                <TextInput
                  label={t('addressPlaceholder')}
                  mode="outlined"
                  disabled={typingBeat}
                  onBlur={onBlur}
                  onChangeText={onChange}
                  value={value}
                  error={!!errors.address}
                  style={styles.input}
                  testID="chat-input-address"
                  {...submitOnEnter}
                />
                {fieldError('address')}
              </>
            )}
          />
        );
      case 'cmtvde':
        return (
          <Controller
            control={control}
            name="cmtvde"
            render={({ field: { onChange, onBlur, value } }) => (
              <>
                <OperatorLicenseInput
                  label={t('cmtvdePlaceholder')}
                  value={value}
                  onChangeText={(text) => {
                    clearErrors('cmtvde');
                    onChange(text);
                  }}
                  onBlur={onBlur}
                  error={!!errors.cmtvde}
                  style={styles.input}
                  testID="chat-input-cmtvde"
                  onSubmitEditing={submitOnEnter.onSubmitEditing}
                  returnKeyType={submitOnEnter.returnKeyType}
                />
                {fieldError('cmtvde')}
              </>
            )}
          />
        );
      case 'dateOfBirth':
        return (
          <Controller
            control={control}
            name="date_of_birth"
            render={({ field: { value, onChange } }) => (
              <>
                <CustomDatePicker
                  label={t('dobPlaceholder')}
                  value={value}
                  onChange={(nextValue) => {
                    clearErrors('date_of_birth');
                    onChange(nextValue);
                  }}
                  error={!!errors.date_of_birth}
                  maxDate={new Date().toISOString().split('T')[0]}
                  disabled={typingBeat}
                  testID="chat-input-date-of-birth"
                />
                {fieldError('date_of_birth')}
              </>
            )}
          />
        );
      case 'payer':
        return (
          <View style={styles.payerOptions}>
            {workCompanyNameRequired && payer === 'self' && <>
              <Text accessibilityRole="alert">{workCompanyText('collectiveNameRequired')}</Text>
              <Controller control={control} name="companyName" render={({ field: { value, onChange, onBlur } }) =>
                <TextInput testID="chat-input-work-company-name" mode="outlined"
                  label={workCompanyText('companyName')} value={value || ''} onBlur={onBlur}
                  onChangeText={(text) => { clearErrors('companyName'); onChange(text); }}
                  maxLength={255} disabled={typingBeat || continuing} style={styles.input} />
              } />
              {fieldError('companyName')}
            </>}
            <Button
              mode={payer === 'company' ? 'contained' : 'outlined'}
              disabled={typingBeat || continuing}
              onPress={() => selectPayer('company')}
              style={styles.payerOption}
              contentStyle={styles.payerOptionContent}
              testID="chat-payer-company"
            >
              {t('payerCompany')}
            </Button>
            <Button
              mode={payer === 'self' ? 'contained' : 'outlined'}
              disabled={typingBeat || continuing}
              onPress={() => selectPayer('self')}
              style={styles.payerOption}
              contentStyle={styles.payerOptionContent}
              testID="chat-payer-self"
            >
              {t('payerSelf')}
            </Button>
            {fieldError('payer' as any)}
          </View>
        );
      case 'company':
        return (
          <>
            {companyLookupLoading && (
              <Text style={{ color: theme.colors.onSurfaceVariant, marginBottom: 8 }}>
                {t('companyLookupLoading') || 'A verificar empresa…'}
              </Text>
            )}
            {companyLookupError && (
              <Text style={{ color: theme.colors.error, marginBottom: 8 }} accessibilityRole="alert">
                {companyLookupError}
              </Text>
            )}

            {/* FOUND: backend resolved the NIPC to a real company. Decision 1 —
                we do NOT ask for a corporate contact and we do NOT surface the
                driver's personal email/phone. Only confirm ownership. */}
            {companyLookup?.found === true && (
              <View style={{ marginBottom: 12 }}>
                <Text variant="titleMedium" style={{ color: theme.colors.primary, marginBottom: 6 }}>
                  {t('companyLookupFoundTitle')}
                </Text>
                <Text variant="bodyMedium" style={{ color: theme.colors.onSurfaceVariant, marginBottom: 12 }}>
                  {t('companyLookupFoundMessage', {
                    name: companyLookup.name || '',
                    nipc: companyLookup.tax_identifier || removeMask((control._formValues as RegisterDriverFormData).companyNipc || ''),
                  })}
                </Text>
                <View style={{ flexDirection: 'row', gap: 12 }}>
                  <Button
                    mode={companyConfirmed ? 'contained' : 'outlined'}
                    disabled={typingBeat}
                    onPress={async () => {
                      // Invited bind fail-closed: Yes only advances when the
                      // looked-up company matches the invitation NIPC.
                      if (hasInvitation) {
                        const expected = removeMask(
                          (matchedInvitation?.companyNipc || invitedCompanyNipcRef.current || '') as string,
                        );
                        const looked = removeMask(String(companyLookup?.tax_identifier || ''));
                        const typed = removeMask(
                          String((control._formValues as RegisterDriverFormData).companyNipc || ''),
                        );
                        if (!expected || typed !== expected || (looked && looked !== expected)) {
                          setCompanyConfirmed(false);
                          setCompanyLookupError(
                            t('companyLookupInviteMismatch') ||
                              'O NIPC não corresponde à empresa do convite. Corrija antes de continuar.',
                          );
                          return;
                        }
                      }
                      setCompanyLookupError(null);
                      const nipcDigits = removeMask(String(
                        companyLookup?.tax_identifier
                          || (control._formValues as RegisterDriverFormData).companyNipc
                          || '',
                      ));
                      // Own-account organic path: persist the confirmed-company
                      // UX draft server-side and await the acknowledgement BEFORE
                      // the explicit payer step. Invited drivers keep their own
                      // bind/accept flow (D-006/D-011). This draft is continuity
                      // only: it never infers payer, coverage, relationship or
                      // invitation, and the fiscal identity comes from the server
                      // (D-007/D-008/D-013/D-031/D-050/D-053/D-055).
                      if (!hasInvitation) {
                        const persisted = await persistConfirmedCompany(nipcDigits);
                        if (!persisted) return;
                      }
                      setCompanyConfirmed(true);
                      // A found company is confirmed: advance to the payer
                      // decision. (handleContinue returns early after the lookup
                      // so the explicit Yes is what moves the flow forward.)
                      advanceTo(stepOrder[stepIndex + 1]);
                    }}
                    style={styles.payerOption}
                    contentStyle={styles.payerOptionContent}
                    testID="chat-company-confirm-yes"
                  >
                    {t('companyLookupConfirmYes')}
                  </Button>
                  <Button
                    mode="outlined"
                    disabled={typingBeat}
                    onPress={async () => {
                      // "No" / correct: clear ONLY the server-side driver_company
                      // draft and await the acknowledgement BEFORE the local
                      // reset, so the corrected choice can never be resurrected
                      // from a stale draft on a later session. Fail-closed: a
                      // failed clear keeps the state (no reset) with an
                      // actionable error. Then reset the lookup so the driver
                      // re-enters the NIPC (canonical resume — no duplicate POST).
                      const cleared = await clearConfirmedCompanyDraft();
                      if (!cleared) return;
                      setCompanyLookup(null);
                      setCompanyConfirmed(false);
                      setValue('companyNipc', '');
                      setValue('companyName', '');
                    }}
                    style={styles.payerOption}
                    contentStyle={styles.payerOptionContent}
                    testID="chat-company-confirm-no"
                  >
                    {t('companyLookupConfirmNo')}
                  </Button>
                </View>
                {companyConfirmed && (
                  <Text style={{ color: theme.colors.primary, marginTop: 8 }}>
                    {t('companyLookupConfirmedHint') || 'Empresa confirmada. Pode continuar.'}
                  </Text>
                )}
              </View>
            )}

            {/* UNKNOWN: NIPC identifies the future company. One contact (email OR
                phone) is enough for both self-pay and company-pay; name is not asked. */}
            {companyLookup && companyLookup.found === false && (
              <>
                <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant, marginBottom: 8 }}>
                  {t('companyContactHintUnknown') || 'Não encontrámos esta empresa. Preencha só um contacto: email ou telefone.'}
                </Text>
                <Controller
                  control={control}
                  name="companyEmail"
                  render={({ field: { onChange, onBlur, value } }) => (
                    <TextInput
                      ref={companyEmailRef}
                      label={t('companyEmailOptional')}
                      mode="outlined"
                      disabled={typingBeat}
                      autoCapitalize="none"
                      keyboardType="email-address"
                      onBlur={onBlur}
                      onChangeText={(text) => { onChange(text); setCompanyStepError(null); }}
                      value={value || ''}
                      style={styles.input}
                      testID="chat-input-company-email"
                      {...focusNext(companyPhoneRef)}
                    />
                  )}
                />
                <Text
                  variant="labelLarge"
                  testID="company-contact-or"
                  style={{ color: theme.colors.onSurfaceVariant, textAlign: 'center', marginVertical: 4 }}
                >
                  {t('companyContactOr')}
                </Text>
                <Controller
                  control={control}
                  name="companyPhone"
                  render={({ field: { onChange, onBlur, value } }) => (
                    <TextInput
                      ref={companyPhoneRef}
                      label={t('companyPhoneOptional')}
                      mode="outlined"
                      disabled={typingBeat}
                      keyboardType="phone-pad"
                      onBlur={onBlur}
                      onChangeText={(text) => { onChange(text); setCompanyStepError(null); }}
                      value={value || ''}
                      style={styles.input}
                      testID="chat-input-company-phone"
                      {...submitOnEnter}
                    />
                  )}
                />
              </>
            )}

            {/* NIPC stays editable after lookup so the driver can correct a
                mismatch (invited bind) or unknown lead without losing the step.
                Alternative contact fields only appear for unknown organic results. */}
            <Controller
              control={control}
              name="companyNipc"
              render={({ field: { onChange, onBlur, value } }) => (
                <TextInput
                  ref={companyNipcRef}
                  label={t('companyNipc')}
                  mode="outlined"
                  disabled={typingBeat}
                  keyboardType="numeric"
                  onBlur={onBlur}
                  onChangeText={(text) => {
                    onChange(applyMaskNipc(text));
                    setCompanyStepError(null);
                    setCompanyLookupError(null);
                    // Changing NIPC invalidates prior lookup/confirm.
                    setWorkCompanyNameRequired(false);
                    setValue('companyName', '');
                    if (companyLookup !== null) {
                      setCompanyLookup(null);
                      setCompanyConfirmed(false);
                    }
                  }}
                  value={value || ''}
                  error={!!errors.companyNipc}
                  style={styles.input}
                  testID="chat-input-company-nipc"
                  {...submitOnEnter}
                />
              )}
            />

            {fieldError('companyNipc')}
            {fieldError('companyEmail')}
            {companyStepError && (
              <Text style={{ color: theme.colors.error, marginTop: 5 }} accessibilityRole="alert">
                {companyStepError}
              </Text>
            )}
          </>
        );
    }
  };

  const { question, hint } = questionFor[stepId];

  const answerSummaryFor = (id: ChatStepId): string => {
    const v = control._formValues as RegisterDriverFormData;
    switch (id) {
      case 'email': return v.email;
      case 'password': return '••••••••';
      case 'name': return v.full_name;
      case 'phone': return `${countryCode} ${v.phoneNumber}`;
      case 'nif': return v.nif;
      case 'address': return v.address;
      case 'cmtvde': return v.cmtvde;
      case 'dateOfBirth': return v.date_of_birth;
      case 'payer':
        return v.payer === 'self' ? t('payerSelf') : t('payerCompany');
      case 'company': return v.companyName || v.companyNipc || '';
    }
  };

  // Post-submit outcome for the organic (no invite) company-pay/self-pay
  // path — C3 (company existed, owner notified) vs C4 (company shell just
  // created, share the link too). Not a chat bubble: a dedicated result
  // screen, same idea as register/success.tsx but with dynamic content.
  if (outcome) {
    const outcomeCopy = getDriverOutcomeCopy(outcome.outcome, outcome.payer);
    return (
      <Surface style={[styles.container, { backgroundColor: theme.colors.background }]}>
        <View style={styles.outcomeWrap}>
          <Text
            variant="headlineSmall"
            style={{ color: theme.colors.primary, textAlign: 'center', marginBottom: 12 }}
            accessibilityRole="header"
            testID="outcome-title"
          >
            {t(outcomeCopy.title)}
          </Text>
          <Text
            variant="bodyMedium"
            style={{ color: theme.colors.onSurfaceVariant, textAlign: 'center', marginBottom: 16 }}
            testID="outcome-message"
          >
            {t(
              outcomeCopy.message,
              { company: outcome.companyName }
            )}
          </Text>
          {outcome.payer === 'self' && (
            <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant, textAlign: 'center', marginBottom: 16 }}>
              {t('outcomeSelfPayHint')}
            </Text>
          )}
          {/* ALL-23: registration is complete; offer the app (web only). */}
          <RegistrationAppStoreLinks style={{ marginBottom: 16 }} />
          {outcome.outcome === 'company_contact_submitted' && (
            <View style={styles.shareBox}>
              <Text
                selectable
                variant="bodySmall"
                style={{ color: theme.colors.onSurfaceVariant, textAlign: 'center', marginBottom: 8 }}
                testID="outcome-share-url"
              >
                {shareUrl}
              </Text>
              <Button mode="outlined" onPress={handleShare} icon="share-variant" testID="outcome-share-button">
                {t('outcomeShareCta')}
              </Button>
            </View>
          )}
          <Button
            mode="contained"
            onPress={() => router.replace(routeForDriverOutcome(outcome.outcome))}
            style={{ marginTop: 24, borderRadius: 12 }}
            contentStyle={{ paddingVertical: 6 }}
            testID="outcome-continue"
          >
            {outcome.outcome === 'company_contact_submitted'
              ? t('outcomeContactContinue')
              : t('outcomeContinue')}
          </Button>
        </View>
      </Surface>
    );
  }

  const registrationHistoryLocked =
    continuing || ((accountReady || resumeMode) && stepId === 'company');

  return (
    <Surface style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <Appbar.Header>
        <Appbar.BackAction
          onPress={() => router.back()}
          disabled={registrationHistoryLocked}
          testID="register-driver-header-back"
        />
        <Appbar.Content title={t('registerDriverTitle')} />
      </Appbar.Header>

      <View style={styles.progressRow}>
        <IconButton
          icon="arrow-left"
          size={20}
          onPress={goBack}
          disabled={registrationHistoryLocked}
          accessibilityLabel={t('back')}
          testID="chat-back"
        />
        <View style={styles.progressBarWrap}>
          <ProgressBar
            progress={(stepIndex + 1) / totalSteps}
            color={theme.colors.primary}
            style={styles.progressBar}
          />
        </View>
        <Text variant="labelMedium" style={{ color: theme.colors.onSurfaceVariant, marginRight: 12 }}>
          {t('chatProgress', { current: stepIndex + 1, total: totalSteps })}
        </Text>
      </View>

      <ChatConversation
        composer={
          <DataSetView
            testID="chat-composer"
            dataSet={{ state: typingBeat ? 'typing' : 'idle' }}
            style={typingBeat ? styles.hiddenComposer : undefined}
            pointerEvents={typingBeat ? 'none' : 'auto'}
            accessibilityElementsHidden={typingBeat}
            importantForAccessibility={typingBeat ? 'no-hide-descendants' : 'auto'}
          >
            {ALL_STEP_IDS.map(id => (
              <View key={id} style={id === stepId ? undefined : styles.hiddenStep}>
                {renderStepInputFor(id)}
              </View>
            ))}
            {stepId !== 'payer' && (
              <Button
                mode="contained"
                onPress={handleContinue}
                loading={isSubmitting || checking || continuing}
                disabled={isSubmitting || checking || continuing || typingBeat}
                style={styles.continueButton}
                contentStyle={styles.continueButtonContent}
                testID="chat-continue"
              >
                {isLastStep ? t('submitButton') : t('chatContinue')}
              </Button>
            )}
          </DataSetView>
        }
      >
        <ChatBubble role="assistant">
          {t(profileRecoveryMode ? 'chatProfileRecoveryIntro' : 'chatDriverIntro')}
        </ChatBubble>
        {stepOrder.slice(0, stepIndex).map(id => (
          <View key={id}>
            <ChatBubble role="assistant">{questionFor[id].question}</ChatBubble>
            <ChatBubble role="user" testID={`chat-answer-${id}`}>{answerSummaryFor(id)}</ChatBubble>
          </View>
        ))}
        {typingBeat ? (
          <TypingIndicator />
        ) : (
          <ChatBubble
            role="assistant"
            hint={hint}
            testID={`chat-question-${stepId}`}
            accessibilityRole="header"
            accessibilityLiveRegion="polite"
          >
            {question}
          </ChatBubble>
        )}
      </ChatConversation>

      <Portal>
        <Dialog
          visible={!!invitationOffer}
          onDismiss={declineMatchedInvitation}
          style={Platform.OS === 'web' ? { maxWidth: 500, width: '90%', alignSelf: 'center' } : {}}
        >
          <Dialog.Title>{t('invitationFoundTitle')}</Dialog.Title>
          <Dialog.Content>
            <Text variant="bodyMedium">
              {t('invitationFoundMessage', {
                company: invitationOffer?.companyName,
              })}
            </Text>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={declineMatchedInvitation} testID="invitation-offer-decline">
              {t('invitationFoundDecline')}
            </Button>
            <Button mode="contained" onPress={acceptMatchedInvitation} testID="invitation-offer-accept">
              {t('invitationFoundAccept')}
            </Button>
          </Dialog.Actions>
        </Dialog>

        <Dialog
          visible={invitationLinkNotice}
          onDismiss={() => {
            setInvitationLinkNotice(false);
            advanceTo('password');
          }}
          style={Platform.OS === 'web' ? { maxWidth: 500, width: '90%', alignSelf: 'center' } : {}}
        >
          <Dialog.Title>{t('invitationLinkRequiredTitle')}</Dialog.Title>
          <Dialog.Content>
            <Text variant="bodyMedium">{t('invitationLinkRequiredMessage')}</Text>
          </Dialog.Content>
          <Dialog.Actions>
            <Button
              mode="contained"
              onPress={() => {
                setInvitationLinkNotice(false);
                advanceTo('password');
              }}
              testID="invitation-link-notice-continue"
            >
              {t('continue')}
            </Button>
          </Dialog.Actions>
        </Dialog>

        <Dialog
          visible={dupDialogVisible}
          onDismiss={() => setDupDialogVisible(false)}
          style={Platform.OS === 'web' ? { maxWidth: 500, width: '90%', alignSelf: 'center' } : {}}
        >
          <Dialog.Title>{t('duplicateIdentityTitle')}</Dialog.Title>
          <Dialog.Content>
            <Text variant="bodyMedium">{dupDialogMessage}</Text>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setDupDialogVisible(false)}>{t('cancel')}</Button>
            <Button
              mode="contained"
              onPress={() => {
                setDupDialogVisible(false);
                router.push({ pathname: '/', params: { prefill_email: dupDialogEmail } });
              }}
            >
              {t('goToLogin')}
            </Button>
          </Dialog.Actions>
        </Dialog>

        <Dialog
          visible={errorDialogVisible}
          onDismiss={() => setErrorDialogVisible(false)}
          style={Platform.OS === 'web' ? { maxWidth: 500, width: '90%', alignSelf: 'center' } : {}}
        >
          <Dialog.Title>{t('error')}</Dialog.Title>
          <Dialog.Content>
            <Text variant="bodyMedium">{errorDialogMessage}</Text>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setErrorDialogVisible(false)}>{t('okButton')}</Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>
    </Surface>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  // D-057 referral code entry, shown inside the signup chat step.
  referralBlock: { marginTop: 12 },
  progressRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  progressBarWrap: {
    flex: 1,
    marginHorizontal: 8,
  },
  progressBar: {
    height: 6,
    borderRadius: 3,
  },
  input: { marginBottom: 10 },
  hiddenStep: { display: 'none' },
  hiddenComposer: { display: 'none' },
  continueButton: {
    marginTop: 16,
    borderRadius: 12,
  },
  continueButtonContent: {
    paddingVertical: 6,
  },
  phoneInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
  },
  countryCodeButton: {
    marginRight: 10,
    minWidth: 80,
  },
  phoneInput: {
    flex: 1,
  },
  payerOptions: {
    gap: 12,
  },
  payerOption: {
    borderRadius: 12,
  },
  payerOptionContent: {
    paddingVertical: 8,
  },
  outcomeWrap: {
    flex: 1,
    justifyContent: 'center',
    padding: 24,
    width: '100%',
    maxWidth: 480,
    alignSelf: 'center',
  },
  shareBox: {
    marginTop: 8,
    alignItems: 'center',
  },
});
